/**
 * editAccessClient — browser-side bridge to the edit-access server router
 * (`src/server/editAccessRequestsRouter.ts`, mounted at
 * `/api/edit-access-requests`).
 *
 * Site edit-lock (#82). All mutating operations are SERVER-side because they
 * write cross-uid paths (`users/{ownerUid}/sites/{siteId}` and the flat
 * `edit_access_requests/{id}` collection) plus fan out notifications + email
 * under the admin SDK. The store actions (requestEditAccess / approveEditRequest
 * / denyEditRequest / adminForceUnlock) call these wrappers; the
 * EditAccessInbox uses {@link subscribeEditAccessRequests} for a live holder
 * feed off the flat collection (rules allow the holder to read where
 * `currentHolderUid == uid`).
 *
 * lockSite / unlockSite are NOT here — those are holder-owned writes on the
 * holder's own site path and flow through the store's `updateSite` (per the
 * blueprint contract), so no server round-trip is needed.
 */

import {
  collection,
  onSnapshot,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../firebase';
import { authedFetch } from '../lib/authedFetch';
import type { EditAccessRequest } from '../types/editAccessRequest';

/** Firestore collection holding the flat edit-access request docs. */
export const EDIT_ACCESS_REQUESTS_COLLECTION = 'edit_access_requests';

async function readErrorMessage(res: Response, fallback: string): Promise<string> {
  let bodyText = '';
  try {
    bodyText = await res.text();
  } catch {
    return `${fallback}: ${res.status}`;
  }
  if (!bodyText.trim()) return `${fallback}: ${res.status}`;
  try {
    const parsed = JSON.parse(bodyText) as { error?: string; message?: string };
    const detail = parsed.error ?? parsed.message ?? bodyText;
    return `${fallback}: ${res.status} ${detail}`;
  } catch {
    return `${fallback}: ${res.status} ${bodyText}`;
  }
}

/**
 * Ask the current holder for edit access on a locked site. Server creates the
 * `edit_access_requests/{id}` doc (status 'open') and notifies + emails the
 * current holder. Returns the new request id.
 */
export async function createEditAccessRequest(
  siteId: string,
  message?: string,
): Promise<string> {
  const res = await authedFetch('/api/edit-access-requests', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId, message }),
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, 'Edit-access request failed'));
  const { requestId } = (await res.json()) as { requestId: string };
  return requestId;
}

/**
 * Holder approves a request — TRANSFERS the lock to the requester (one holder
 * at a time). Server rewrites the site lock fields to the requester, marks the
 * request 'approved', and notifies + emails the requester.
 */
export async function approveEditAccessRequest(requestId: string): Promise<void> {
  const res = await authedFetch(
    `/api/edit-access-requests/${encodeURIComponent(requestId)}/approve`,
    { method: 'POST' },
  );
  if (!res.ok) throw new Error(await readErrorMessage(res, 'Approve failed'));
}

/**
 * Holder denies a request — lock stays with the current holder. Server marks
 * the request 'denied' and notifies + emails the requester.
 */
export async function denyEditAccessRequest(requestId: string): Promise<void> {
  const res = await authedFetch(
    `/api/edit-access-requests/${encodeURIComponent(requestId)}/deny`,
    { method: 'POST' },
  );
  if (!res.ok) throw new Error(await readErrorMessage(res, 'Deny failed'));
}

/**
 * Platform-admin (isAdmin) or company-admin (companyRole === 'admin') force
 * unlock — clears the lock fields via `deleteField()` server-side and notifies
 * + emails the displaced holder.
 */
export async function forceUnlockSite(siteId: string): Promise<void> {
  const res = await authedFetch(
    `/api/edit-access-requests/${encodeURIComponent(siteId)}/force-unlock`,
    { method: 'POST' },
  );
  if (!res.ok) throw new Error(await readErrorMessage(res, 'Force-unlock failed'));
}

/**
 * Live subscription to the OPEN edit-access requests addressed to `holderUid`
 * (the current lock holder). Used by the EditAccessInbox popover so the holder
 * sees incoming requests without the 5-minute notification poll. Returns the
 * Firestore unsubscribe handle.
 *
 * Rules permit the holder to read where `currentHolderUid == uid`; the status
 * filter is applied client-side WITHOUT a composite index requirement by
 * querying on `currentHolderUid` only and filtering 'open' in the callback —
 * keeping the live feed index-free.
 */
export function subscribeEditAccessRequests(
  holderUid: string,
  onChange: (requests: EditAccessRequest[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, EDIT_ACCESS_REQUESTS_COLLECTION),
    where('currentHolderUid', '==', holderUid),
  );
  return onSnapshot(
    q,
    (snap) => {
      const requests: EditAccessRequest[] = [];
      snap.forEach((d) => {
        const data = d.data() as Omit<EditAccessRequest, 'id'>;
        if (data.status === 'open') requests.push({ ...data, id: d.id });
      });
      requests.sort((a, b) => (a.requestedAt < b.requestedAt ? 1 : -1));
      onChange(requests);
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  );
}
